import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { HTTPMethod, request } from "../fetchHelper";
import { GatewayError } from "../gateway-client/gatewayClient";
import {
  ThemeFileType,
  ThemeErrorType,
  AllowedImageType,
  GetThemeFileMetadataResponse,
  GetThemeFileResponse,
  UploadThemeImageResponse,
  UpdateThemeImageResponse,
} from "./types";

export class ThemeValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ThemeValidationError";
    this.type = ThemeErrorType.VALIDATION_ERROR;

    // ES5 does not fully support subclassing built-in types, instanceof operator doesn't function as expected for custom classes that extend built-in types like Error.
    // Explicitly setting prototype ensures instanceof correctly identifies instances of custom class and its superclasses
    Object.setPrototypeOf(this, ThemeValidationError.prototype);
  }

  type: ThemeErrorType;
}

interface UploadThemeImageParams {
  file: File;
  fileType: ThemeFileType;
  siteId: string;
}

export async function uploadThemeImage({
  file,
  fileType,
  siteId,
}: UploadThemeImageParams): Promise<UploadThemeImageResponse> {
  if (!Object.values(AllowedImageType).includes(file.type as AllowedImageType)) {
    throw new ThemeValidationError("File must be PNG, JPEG, or GIF");
  }

  const formData = new FormData();
  formData.append("file", file);

  const response = await request<UploadThemeImageResponse>({
    url: `gateway/api/assist/rovo/v1/extension/theme/file/${fileType}`,
    options: {
      method: HTTPMethod.POST as HTTPMethod.POST,
      credentials: "include" as RequestCredentials,
      extraHeaders: new Headers({
        "X-Product": "rovo",
        "X-Experience-Id": "ai-mate",
        "X-Cloudid": siteId,
      }),
      body: formData,
    },
  });

  return response;
}

interface DeleteThemeImageParams {
  fileType: ThemeFileType;
  siteId: string;
}

export async function deleteThemeImage({ fileType, siteId }: DeleteThemeImageParams): Promise<void> {
  await request<Record<string, never>>({
    url: `gateway/api/assist/rovo/v1/extension/theme/file/${fileType}`,
    options: {
      method: HTTPMethod.DELETE as HTTPMethod.DELETE,
      credentials: "include" as RequestCredentials,
      extraHeaders: new Headers({
        "X-Product": "rovo",
        "X-Experience-Id": "ai-mate",
        "X-Cloudid": siteId,
      }),
    },
  });
}

interface UpdateThemeImageParams {
  file: File;
  fileType: ThemeFileType;
  siteId: string;
}

export async function updateThemeImage({
  file,
  fileType,
  siteId,
}: UpdateThemeImageParams): Promise<UpdateThemeImageResponse> {
  if (!Object.values(AllowedImageType).includes(file.type as AllowedImageType)) {
    throw new ThemeValidationError("File must be PNG, JPEG, or GIF");
  }

  const formData = new FormData();
  formData.append("file", file);

  const response = await request<UpdateThemeImageResponse>({
    url: `gateway/api/assist/rovo/v1/extension/theme/file/${fileType}`,
    options: {
      method: HTTPMethod.PUT as HTTPMethod.PUT,
      credentials: "include" as RequestCredentials,
      extraHeaders: new Headers({
        "X-Product": "rovo",
        "X-Experience-Id": "ai-mate",
        "X-Cloudid": siteId,
      }),
      body: formData,
    },
  });

  return response;
}

async function getNtpThemeFileMetadata(siteId: string): Promise<GetThemeFileMetadataResponse> {
  const response = await request<GetThemeFileMetadataResponse>({
    url: "gateway/api/assist/rovo/v1/extension/theme/file/metadata",
    options: {
      method: "GET" as HTTPMethod.GET,
      credentials: "include" as RequestCredentials,
      extraHeaders: new Headers({
        "X-Product": "rovo",
        "X-Experience-Id": "ai-mate",
        "X-Cloudid": siteId,
      }),
    },
  });

  return response;
}

type UseNtpThemeFileMetadataParams = {
  siteId: string;
};

export function useNtpThemeFileMetadata({
  siteId,
}: UseNtpThemeFileMetadataParams): UseQueryResult<GetThemeFileMetadataResponse, GatewayError> {
  const queryFn = (): Promise<GetThemeFileMetadataResponse> => getNtpThemeFileMetadata(siteId);

  return useQuery({
    queryKey: ["getNtpThemeFileMetadata", siteId],
    queryFn,
    refetchOnWindowFocus: false,
    enabled: true,
  });
}

type NtpThemeFileParams = {
  siteId: string;
  fileType: ThemeFileType;
  s3Etag: string;
};

async function getNtpThemeFile({ siteId, fileType, s3Etag }: NtpThemeFileParams): Promise<GetThemeFileResponse> {
  const response = await request<{ body: ReadableStream }>({
    url: `gateway/api/assist/rovo/v1/extension/theme/file/${fileType}`,
    options: {
      method: "GET" as HTTPMethod.GET,
      credentials: "include" as RequestCredentials,
      extraHeaders: new Headers({
        "Content-Type": "image",
        "X-Product": "rovo",
        "X-Experience-Id": "ai-mate",
        "X-Cloudid": siteId,
        "X-ETag": s3Etag,
      }),
    },
  });

  const reader = response.body.getReader();
  const chunks: BlobPart[] = [];

  const blob = await reader.read().then(async function process({ done, value }): Promise<Blob> {
    if (done) {
      return new Blob(chunks);
    }

    chunks.push(value);
    return reader.read().then(process);
  });

  const url = URL.createObjectURL(blob);

  return {
    url,
  };
}

type UseNtpThemeFileParams = {
  siteId: string;
  fileType: ThemeFileType;
  s3Etag: string;
};

export function useNtpThemeFile({
  siteId,
  fileType,
  s3Etag,
}: UseNtpThemeFileParams): UseQueryResult<GetThemeFileResponse, GatewayError> {
  return useQuery({
    queryKey: ["getNtpThemeFile", siteId, fileType, s3Etag],
    queryFn: () => getNtpThemeFile({ siteId, fileType, s3Etag }),
    refetchOnWindowFocus: false,
  });
}
